import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import KanbanCard from './KanbanCard';

const ARCHIVE_COLOR = '#78716c';

const ArchiveColumn = ({ items, loading, onUnarchive }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { setNodeRef, isOver } = useDroppable({
    id: 'column-archive',
    data: { columnId: 'archive' },
  });

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase().trim();
    return items.filter((item) => {
      const firmName =
        item.job?.contract?.firm?.firm_name || item.job?.firm_short_name || '';
      const jobTitle = item.job?.job_title || '';
      return `${firmName} ${jobTitle}`.toLowerCase().includes(q);
    });
  }, [items, search]);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 300,
        minWidth: 300,
        maxWidth: 300,
        bgcolor: isOver ? 'rgba(120, 113, 108, 0.06)' : '#fafaf9',
        borderRadius: '12px',
        border: '2px dashed',
        borderColor: isOver ? '#ef4444' : '#d6d3d1',
        flexShrink: 0,
        transition: 'all 0.2s ease',
        boxShadow: isOver ? '0 0 0 2px rgba(239,68,68,0.15)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1.25,
          bgcolor: '#e7e5e4',
          borderBottom: '1px solid #d6d3d1',
        }}
      >
        <ArchiveIcon sx={{ fontSize: '1.1rem', color: ARCHIVE_COLOR }} />
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.82rem',
            flex: 1,
            color: '#44403c',
          }}
        >
          {t('reportManagement.archive.title', { defaultValue: 'Arşiv' })}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 22,
            height: 22,
            borderRadius: '6px',
            bgcolor: '#d6d3d1',
            color: '#57534e',
            fontSize: '0.72rem',
            fontWeight: 700,
            px: 0.5,
          }}
        >
          {filteredItems.length}
        </Box>
      </Box>

      {/* Search */}
      {items.length > 3 && (
        <Box sx={{ px: 1, py: 1, bgcolor: '#e7e5e4' }}>
          <TextField
            size="small"
            fullWidth
            placeholder={t('reportManagement.archive.search', { defaultValue: 'Ara...' })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1rem', color: '#a8a29e' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                fontSize: '0.8rem',
                '& fieldset': { borderColor: '#d6d3d1' },
              },
              '& .MuiInputBase-input': { py: 0.75 },
            }}
          />
        </Box>
      )}

      {/* Cards */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          py: 1,
          minHeight: 100,
          maxHeight: 'calc(100vh - 370px)',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#d6d3d1', borderRadius: 2 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#a8a29e' }} />
          </Box>
        )}

        {!loading && (
          <SortableContext
            items={filteredItems.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {filteredItems.map((item) => (
              <KanbanCard
                key={item.id}
                item={item}
                columnColor={ARCHIVE_COLOR}
                isRoot={false}
                onUnarchive={onUnarchive}
              />
            ))}
          </SortableContext>
        )}

        {!loading && filteredItems.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              color: '#a8a29e',
            }}
          >
            <ArchiveIcon sx={{ fontSize: '1.5rem', color: '#d6d3d1', mb: 0.5 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, textAlign: 'center' }}>
              {t('reportManagement.archive.empty', { defaultValue: 'Arşiv boş' })}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ArchiveColumn;
